import axios from "axios";

// Baked in at build time. Fall back to the production API so a missing build
// variable does not silently point the app at "undefined/api".
const BASE = (process.env.REACT_APP_BACKEND_URL || "https://api.choremouse.app").replace(/\/+$/, "");
export const API = `${BASE}/api`;

export const PARENT_KEY = "choremouse_token";
export const KID_KEY = "choremouse_kid_token";
export const ACCESS_PAUSED_EVENT = "choremouse:access-paused";
export const SESSION_CHANGED_EVENT = "choremouse:session-changed";

// Safe storage: falls back to in-memory when localStorage is blocked
// (e.g. browser set to "Block all cookies", which also disables localStorage).
const mem = {};
function notifySession(key) {
  if ((key === PARENT_KEY || key === KID_KEY) && typeof window !== "undefined") {
    // Consumers need to invalidate private data, never the credential itself.
    window.dispatchEvent(new CustomEvent(SESSION_CHANGED_EVENT, { detail: { tokenKey: key } }));
  }
}
export const storage = {
  get(k) {
    try { const v = localStorage.getItem(k); if (v != null) return v; }
    catch (e) { console.debug("storage: read blocked, using memory", e); }
    return k in mem ? mem[k] : null;
  },
  set(k, v) {
    mem[k] = v;
    try { localStorage.setItem(k, v); }
    catch (e) { console.debug("storage: write blocked, kept in memory", e); }
    notifySession(k);
  },
  remove(k) {
    delete mem[k];
    try { localStorage.removeItem(k); }
    catch (e) { console.debug("storage: remove blocked", e); }
    notifySession(k);
  },
};

if (typeof window !== "undefined") {
  window.addEventListener("storage", (event) => {
    // A different tab's sign-out must also clear this tab's memory fallback.
    if (event.key === null) {
      delete mem[PARENT_KEY];
      delete mem[KID_KEY];
    } else if (event.key === PARENT_KEY || event.key === KID_KEY) {
      if (event.newValue === null) delete mem[event.key];
      else mem[event.key] = event.newValue;
    }
  });
}

function make(tokenKey) {
  const instance = axios.create({ baseURL: API });
  instance.interceptors.request.use((config) => {
    if (tokenKey) {
      const t = storage.get(tokenKey);
      if (t) config.headers.Authorization = `Bearer ${t}`;
    }
    return config;
  });
  instance.interceptors.response.use((response) => response, (error) => {
    const detail = error?.response?.data?.detail;
    if (tokenKey && error?.response?.status === 402 && detail?.code === "subscription_required") {
      window.dispatchEvent(new CustomEvent(ACCESS_PAUSED_EVENT, { detail: { tokenKey, access: detail } }));
    }
    return Promise.reject(error);
  });
  return instance;
}

export const publicApi = make(null);
export const parentApi = make(PARENT_KEY);
export const kidApi = make(KID_KEY);

export function apiError(err) {
  const detail = err?.response?.data?.detail;
  if (detail == null) return err?.message || "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  if (detail && typeof detail.message === "string") return detail.message;
  return String(detail);
}
